import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
    DataTableAction,
    DataTableCustomized,
    createColumns,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import EditIcons from '@/icons/EditIcons'
import dayjs from 'dayjs'

import CustomerIcons from '@/icons/CustomerIcons'
import { PositionEditModalProps } from '@/modals/PositionModals/Edit'
import { deletePosition, getPositionsByDepartment } from '../../services'
import { Employee, Position } from '../../types/response'

interface PositionsTabProps {
    departmentId: number
}

const PositionsTab = ({ departmentId }: PositionsTabProps) => {
    const { t } = useTranslation('employee')
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()

    // Fetch positions
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.POSITIONS, departmentId],
        queryFn: () => getPositionsByDepartment(departmentId),
        enabled: !!departmentId,
    })

    const positions = data?.results || []

    // Delete mutation
    const { mutateAsync: doDeletePosition } = useMutationWithHandler(
        { mutationFn: deletePosition },
        {
            reactQueryKeys: QUERY_KEYS.POSITIONS,
            successMessage: 'position_deleted_success',
            errorMessage: 'error_occurred',
            onSuccess: () => refetch(),
        }
    )

    // Columns
    const columns = useMemo(
        () =>
            createColumns<Position>([
                {
                    key: 'position_name',
                    title: t('position_name'),
                    width: 250,
                    headerClassName: 'text-left',
                },
                {
                    key: 'position_code',
                    title: t('position_code'),
                    width: 150,
                    headerClassName: 'text-left',
                    render: (value) => value || '-',
                },
                {
                    key: 'keycloak_default_group_paths',
                    title: 'Keycloak group',
                    width: 160,
                    headerClassName: 'text-left',
                    render: (value) => value?.join(', ') || '-',
                },
                {
                    key: 'employees',
                    title: t('employees'),
                    width: 120,
                    headerClassName: 'text-center',
                    cellClassName: 'text-center',
                    render: (value: Employee[] | undefined) => {
                        const count = value?.length || 0
                        if (count === 0) return '-'

                        return (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                                <CustomerIcons className="w-4 h-4" />
                                {count}
                            </span>
                        )
                    },
                },
                {
                    key: 'position_created_at',
                    title: t('created_at'),
                    width: 150,
                    headerClassName: 'text-center',
                    cellClassName: 'text-center',
                    render: (value) =>
                        value ? dayjs(value).format('DD/MM/YYYY') : '-',
                },
            ]),
        [t]
    )

    // Actions
    const actions: DataTableAction<Position>[] = useMemo(
        () => [
            {
                key: 'edit',
                label: t('edit'),
                icon: <EditIcons className="w-4 h-4" />,
                onClick: (row) =>
                    setModal<PositionEditModalProps>(
                        'PositionModals/Edit/index',
                        {
                            position: row,
                            refetch,
                        }
                    ),
            },
            {
                key: 'delete',
                label: t('delete'),
                icon: <DeleteOutlineIcons className="w-4 h-4" />,
                variant: 'danger',
                onClick: (row) =>
                    setConfirmation({
                        open: true,
                        title: t('delete_position'),
                        description: t('delete_position_confirm'),
                        onConfirm: async () => {
                            await doDeletePosition(row.position_id)
                        },
                    }),
            },
        ],
        [t, setModal, setConfirmation, doDeletePosition, refetch]
    )

    const stateComponent = renderState()

    return (
        <div className="space-y-2">
            {/* Table */}
            {stateComponent || (
                <DataTableCustomized
                    columns={columns}
                    rows={positions}
                    rowKey={(row) => row.position_id}
                    size="small"
                    actions={actions}
                    expandable={{
                        rowExpandable: (row) =>
                            (row.employees?.length || 0) > 0,
                        expandedRowRender: (row) => (
                            <DataTableCustomized
                                columns={[
                                    {
                                        key: 'employee_name',
                                        title: t('employee_name'),
                                        headerClassName: 'text-left',
                                    },
                                    {
                                        key: 'employee_code',
                                        title: t('employee_code'),
                                        headerClassName: 'text-left',
                                        render: (value) => value || '-',
                                    },
                                ]}
                                rows={row.employees || []}
                                rowKey={(emp) => emp.employee_id}
                                size="small"
                            />
                        ),
                    }}
                />
            )}
        </div>
    )
}

export default PositionsTab
